import { useCallback, useContext, useEffect, useRef, useState } from "react";

import {
  Box,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Chip,
  useTheme
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";

import * as Sentry from "@sentry/react";

import ScoutMark from "../../atoms/icons/ScoutMark";
import {
  UserMessage,
  AssistantMessage,
  ThinkingIndicator
} from "../scout/ScoutMessages";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import { useCachedDisplayData } from "../../../helpers/firebase";
import { analyticsTrack } from "../../../helpers/analytics";
import usePermissions from "../../../helpers/hooks/usePermissions";

import { SurveyContext } from "../survey-shape/SurveyContext";
import {
  applyFieldUpdate,
  hasExistingValue,
  getFieldDisplayValue,
  getFieldConfirmPrompt
} from "./scoutFieldHandlers";

const RAIL_WIDTH = 48;

const buildCurrentValuesSnapshot = (context, currentStep) => {
  const {
    vendorData,
    facilities,
    transactions,
    vehicleFuelData,
    scopeTons,
    selectedYear
  } = context;
  return {
    "survey:year": selectedYear,
    "vendor:industry": vendorData?.industry,
    "vendor:revenueYears": vendorData?.revenueYears,
    "vendor:employeeCount": vendorData?.employeeCount,
    "vendor:isRemote": vendorData?.isRemote,
    "vendor:address": vendorData?.address,
    "vendor:hasNoCompanyVehicles": vendorData?.hasNoCompanyVehicles,
    facilities,
    transactions,
    "vehicle-fuel:fuelVolume": vehicleFuelData?.fuelVolume,
    "vehicle-fuel:fuelDollars": vehicleFuelData?.fuelDollars,
    scopeTons,
    currentStepFieldId: currentStep?.fieldId
  };
};

const buildConfirmPrompt = (currentStep, displayValue) => {
  const titleLower = currentStep?.title
    ? currentStep.title.toLowerCase()
    : "this";
  return `I've got ${displayValue} for ${titleLower} from last time — still accurate?`;
};

const isAckOnlyResponse = (response) => {
  if (!response) {
    return true;
  }
  const trimmed = response.trim();
  if (!trimmed) {
    return true;
  }
  if (trimmed.includes("?")) {
    return false;
  }
  return trimmed.length < 80;
};

const buildStepQuestion = (currentStep, { context } = {}) => {
  if (!currentStep) {
    return null;
  }
  if (currentStep.fieldId && context) {
    const customConfirm = getFieldConfirmPrompt(currentStep.fieldId, context);
    if (customConfirm) {
      return customConfirm;
    }
    const displayValue = getFieldDisplayValue(currentStep.fieldId, context);
    if (displayValue) {
      return buildConfirmPrompt(currentStep, displayValue);
    }
  }
  if (currentStep.prompt) {
    return currentStep.prompt;
  }
  return null;
};

const buildInitialOpener = (
  currentStep,
  selectedYear,
  params,
  { isReturning } = {}
) => {
  const question = buildStepQuestion(currentStep, params);
  if (!question) {
    return "Hi! I'm Scout — I'll guide you through this survey. We can chat through each question, or you can fill out the form on the right directly.";
  }
  const isYearStep = currentStep?.fieldId === "survey:year";
  const yearPhrase = !isYearStep && selectedYear ? ` for ${selectedYear}` : "";
  if (isReturning) {
    const stepLabel = currentStep?.title
      ? currentStep.title.toLowerCase()
      : "your next section";
    return `Welcome back! Let's pick up where you left off — next up is ${stepLabel}. ${question}`;
  }
  return `Hi! I'm Scout — I'll help you put together a quick estimate of your company's emissions${yearPhrase}. Don't worry if you don't have every number on hand; ballparks work great, and you can skip anything you're not sure about. ${question}`;
};

const hasExistingSurveyProgress = (context, selectedYear) => {
  if (!context || !selectedYear) {
    return false;
  }
  const vendorSurveys = context.vendorData?.vendorSurveys || [];
  return vendorSurveys.some(
    ({ year }) => Number(year) === Number(selectedYear)
  );
};

const PanelHeader = ({ isOpen, onToggle, onClose, showCloseIcon }) => {
  const { palette } = useTheme();
  return (
    <Box
      p={1.5}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      style={{
        flexShrink: 0,
        borderBottom: `1px solid ${palette.backgroundGray.dark}`
      }}
    >
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <ScoutMark size={28} />
        {isOpen && (
          <Typography variant="subtitle1" color="textPrimary">
            Scout
          </Typography>
        )}
      </Box>
      {isOpen && showCloseIcon && (
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {isOpen && !showCloseIcon && (
        <Tooltip title="Collapse Scout">
          <IconButton size="small" onClick={onToggle}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!isOpen && (
        <Tooltip title="Expand Scout">
          <IconButton size="small" onClick={onToggle}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const PanelTranscript = ({ messages, isLoading, transcriptRef }) => (
  <Box
    ref={transcriptRef}
    px={2}
    py={1}
    flexGrow={1}
    display="flex"
    flexDirection="column"
    style={{ gap: 12, overflowY: "auto", minHeight: 0 }}
  >
    {messages.map((msg, idx) =>
      msg.role === "user" ? (
        <UserMessage key={`survey-msg-${idx}`} content={msg.content} />
      ) : (
        <AssistantMessage key={`survey-msg-${idx}`} content={msg.content} />
      )
    )}
    {isLoading && <ThinkingIndicator />}
  </Box>
);

const PanelFooter = ({
  input,
  setInput,
  onKeyDown,
  isLoading,
  canEdit,
  inputRef,
  showSkipControls,
  showContinue,
  continueLabel,
  onSkip,
  onUnknown,
  onContinue
}) => {
  const controlsDisabled = isLoading || !canEdit;
  return (
    <Box p={2} style={{ flexShrink: 0 }} display="flex" flexDirection="column">
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder={
          canEdit
            ? "Answer or ask Scout anything"
            : "You have view-only access to this survey"
        }
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        multiline
        maxRows={4}
        disabled={controlsDisabled}
        inputRef={inputRef}
      />
      {(showSkipControls || showContinue) && (
        <Box display="flex" flexWrap="wrap" mt={1} style={{ gap: 8 }}>
          {showContinue && (
            <Chip
              label={continueLabel || "Continue"}
              color="primary"
              size="small"
              onClick={onContinue}
              disabled={controlsDisabled}
            />
          )}
          {showSkipControls && (
            <Chip
              label="Skip this question"
              variant="outlined"
              size="small"
              onClick={onSkip}
              disabled={controlsDisabled}
            />
          )}
          {showSkipControls && (
            <Chip
              label="I don't know"
              variant="outlined"
              size="small"
              onClick={onUnknown}
              disabled={controlsDisabled}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const PanelBody = ({
  messages,
  isLoading,
  transcriptRef,
  input,
  setInput,
  onKeyDown,
  canEdit,
  inputRef,
  showSkipControls,
  showContinue,
  continueLabel,
  onSkip,
  onUnknown,
  onContinue
}) => (
  <>
    <PanelTranscript
      messages={messages}
      isLoading={isLoading}
      transcriptRef={transcriptRef}
    />
    <PanelFooter
      input={input}
      setInput={setInput}
      onKeyDown={onKeyDown}
      isLoading={isLoading}
      canEdit={canEdit}
      inputRef={inputRef}
      showSkipControls={showSkipControls}
      showContinue={showContinue}
      continueLabel={continueLabel}
      onSkip={onSkip}
      onUnknown={onUnknown}
      onContinue={onContinue}
    />
  </>
);

const SurveyScoutPanel = ({ currentStep }) => {
  const { palette } = useTheme();
  const context = useContext(SurveyContext);
  const { canEdit } = usePermissions();
  const {
    vendorId,
    selectedYear,
    scoutConversationId,
    ensureConversationId,
    advanceCurrentStep,
    onNextStep,
    skipQuestion,
    markUnknown,
    clearSkipUnknownMarkers,
    touchField,
    isScoutCollapsed,
    setIsScoutCollapsed,
    surveyCompletionStatus,
    skippedFields = [],
    unknownFields = []
  } = context;

  const hasIncompleteFields =
    (skippedFields || []).length > 0 || (unknownFields || []).length > 0;

  const persistTurnsToConversation = useCallback(
    async (turns) => {
      if (!turns || !turns.length) {
        return;
      }
      try {
        const conversationId = await ensureConversationId();
        if (!conversationId) {
          return;
        }
        await fetchOurApi({
          path: "/agents/record-survey-turns",
          method: "POST",
          data: {
            companyId: vendorId,
            conversationId,
            surveyYear: selectedYear,
            turns
          }
        });
      } catch (err) {
        Sentry.captureException(err, {
          tags: { feature: "scout-survey-turn-persist" },
          extra: {
            surveyYear: selectedYear,
            fieldId: currentStep?.fieldId,
            turnCount: turns.length
          }
        });
      }
    },
    [ensureConversationId, vendorId, selectedYear, currentStep?.fieldId]
  );

  const carriedOverFieldsRef = useRef(new Map());
  const carriedOverFieldId = currentStep?.fieldId || null;
  useEffect(() => {
    if (!carriedOverFieldId) {
      return;
    }
    if (carriedOverFieldsRef.current.has(carriedOverFieldId)) {
      return;
    }
    carriedOverFieldsRef.current.set(
      carriedOverFieldId,
      hasExistingValue({ fieldId: carriedOverFieldId, context })
    );
  }, [carriedOverFieldId, context]);

  const currentStepCarriedOver =
    !!carriedOverFieldId &&
    carriedOverFieldsRef.current.get(carriedOverFieldId) === true;

  const [conversations, conversationsLoading] = useCachedDisplayData(
    "agent-conversations"
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const desktopTranscriptRef = useRef(null);
  const mobileTranscriptRef = useRef(null);
  const desktopReplyInputRef = useRef(null);
  const mobileReplyInputRef = useRef(null);
  const hydratedRef = useRef(false);
  const lastAnnouncedFieldIdRef = useRef(null);
  const aggregationDoneAnnouncedRef = useRef(false);
  const pendingAutoAdvanceRef = useRef(null);

  useEffect(() => {
    if (aggregationDoneAnnouncedRef.current) {
      return;
    }
    if (
      surveyCompletionStatus !== "done" &&
      surveyCompletionStatus !== "error"
    ) {
      return;
    }
    if (!currentStep?.isFinalStep) {
      return;
    }
    aggregationDoneAnnouncedRef.current = true;
    const followUp =
      surveyCompletionStatus === "done"
        ? "All set — your dashboard is ready. Taking you there now! 👋"
        : "I hit a snag finalizing your numbers — our team has been notified and will follow up. You can still head to your dashboard.";
    setMessages((prev) => [...prev, { role: "assistant", content: followUp }]);
    persistTurnsToConversation([{ role: "assistant", content: followUp }]);
  }, [surveyCompletionStatus, currentStep, persistTurnsToConversation]);

  useEffect(() => {
    if (hydratedRef.current) {
      return;
    }
    if (!currentStep) {
      return;
    }
    if (conversationsLoading) {
      return;
    }
    const persistedConv = scoutConversationId
      ? (conversations || []).find((c) => c.id === scoutConversationId)
      : null;
    if (scoutConversationId && !persistedConv) {
      return;
    }
    const persistedTurns = persistedConv?.turns || [];
    if (persistedTurns.length > 0) {
      hydratedRef.current = true;
      setMessages(
        persistedTurns.map(({ role, content }) => ({ role, content }))
      );
      lastAnnouncedFieldIdRef.current = currentStep?.fieldId || null;
      return;
    }
    hydratedRef.current = true;
    const isFirstFieldStep = currentStep?.fieldId === "survey:year";
    const isReturning =
      !isFirstFieldStep && hasExistingSurveyProgress(context, selectedYear);
    const opener = buildInitialOpener(
      currentStep,
      selectedYear,
      { context },
      { isReturning }
    );
    if (opener) {
      setMessages([{ role: "assistant", content: opener }]);
      persistTurnsToConversation([{ role: "assistant", content: opener }]);
    }
    lastAnnouncedFieldIdRef.current = currentStep?.fieldId || null;
  }, [
    scoutConversationId,
    conversations,
    conversationsLoading,
    currentStep,
    selectedYear,
    context,
    persistTurnsToConversation
  ]);

  useEffect(() => {
    const scrollToBottom = (node) => {
      if (!node) {
        return;
      }
      node.scrollTop = node.scrollHeight;
    };
    requestAnimationFrame(() => {
      scrollToBottom(desktopTranscriptRef.current);
      scrollToBottom(mobileTranscriptRef.current);
    });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isLoading || messages.length === 0) {
      return;
    }
    desktopReplyInputRef.current?.focus();
    mobileReplyInputRef.current?.focus();
  }, [isLoading, messages.length]);

  useEffect(() => {
    if (!hydratedRef.current) {
      return;
    }
    if (!currentStep?.fieldId) {
      const terminalKey = "__terminal__";
      if (
        currentStep?.isFinalStep &&
        lastAnnouncedFieldIdRef.current !== terminalKey
      ) {
        const yearPhrase = selectedYear ? ` for ${selectedYear}` : "";
        const terminalMessage = hasIncompleteFields
          ? `You've reached the end of the survey${yearPhrase}. A few questions were skipped or marked unknown, so your emissions profile isn't fully complete yet — you can fill those in later on your data completeness page. I'm crunching what we have to put together your dashboard.`
          : `🎉 Nice work — your emissions profile${yearPhrase} is complete! That's the foundation of your carbon footprint. I'm crunching the numbers now to put together your dashboard.`;
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: terminalMessage }
        ]);
        persistTurnsToConversation([
          { role: "assistant", content: terminalMessage }
        ]);
        lastAnnouncedFieldIdRef.current = terminalKey;
      }
      return;
    }
    if (lastAnnouncedFieldIdRef.current === currentStep.fieldId) {
      return;
    }
    const question = buildStepQuestion(currentStep, { context });
    if (!question) {
      lastAnnouncedFieldIdRef.current = currentStep.fieldId;
      return;
    }
    setMessages((prev) => [...prev, { role: "assistant", content: question }]);
    persistTurnsToConversation([{ role: "assistant", content: question }]);
    lastAnnouncedFieldIdRef.current = currentStep.fieldId;
  }, [
    currentStep,
    context,
    selectedYear,
    persistTurnsToConversation,
    hasIncompleteFields
  ]);

  const pendingAdvanceFieldId = currentStep?.fieldId || null;
  const pendingAdvanceDisplayValue = pendingAdvanceFieldId
    ? getFieldDisplayValue(pendingAdvanceFieldId, context)
    : null;
  useEffect(() => {
    const pending = pendingAutoAdvanceRef.current;
    if (!pending) {
      return;
    }
    if (pending.fieldId !== pendingAdvanceFieldId) {
      pendingAutoAdvanceRef.current = null;
      return;
    }
    if (!pendingAdvanceDisplayValue) {
      return;
    }
    pendingAutoAdvanceRef.current = null;
    if (touchField) {
      touchField(pending.fieldId);
    }
    if (clearSkipUnknownMarkers) {
      clearSkipUnknownMarkers(pending.fieldId);
    }
    analyticsTrack("survey_scout_auto_advance_after_updates", {
      companyId: vendorId,
      surveyYear: selectedYear,
      fieldId: pending.fieldId
    });
    if (advanceCurrentStep) {
      advanceCurrentStep();
    }
  }, [
    pendingAdvanceDisplayValue,
    pendingAdvanceFieldId,
    touchField,
    clearSkipUnknownMarkers,
    advanceCurrentStep,
    vendorId,
    selectedYear
  ]);

  const handleFieldUpdate = (fieldUpdate) => {
    if (!fieldUpdate) {
      return;
    }
    applyFieldUpdate({ fieldUpdate, context });
    if (touchField) {
      touchField(fieldUpdate.fieldId);
    }
    if (clearSkipUnknownMarkers) {
      clearSkipUnknownMarkers(fieldUpdate.fieldId);
    }
    analyticsTrack("survey_scout_field_autopopulated", {
      companyId: vendorId,
      surveyYear: selectedYear,
      fieldId: fieldUpdate.fieldId,
      confidence: fieldUpdate.confidence
    });
  };

  const handleSkip = async () => {
    if (!canEdit || !currentStep?.fieldId) {
      return;
    }
    const skipTurns = [
      { role: "user", content: "Skip this question" },
      {
        role: "assistant",
        content:
          "No problem — I'll mark this as skipped and we'll come back to it on your data completeness page."
      }
    ];
    setMessages((prev) => [...prev, ...skipTurns]);
    await persistTurnsToConversation(skipTurns);
    if (skipQuestion) {
      await skipQuestion(currentStep.fieldId);
    }
    if (touchField) {
      touchField(currentStep.fieldId);
    }
    analyticsTrack("survey_field_skipped", {
      companyId: vendorId,
      surveyYear: selectedYear,
      fieldId: currentStep.fieldId
    });
    if (onNextStep) {
      onNextStep();
    }
  };

  const handleUnknown = async () => {
    if (!canEdit || !currentStep?.fieldId) {
      return;
    }
    const unknownTurns = [
      { role: "user", content: "I don't know" },
      {
        role: "assistant",
        content:
          "Got it — I'll flag this as a gap so you can see what's missing later."
      }
    ];
    setMessages((prev) => [...prev, ...unknownTurns]);
    await persistTurnsToConversation(unknownTurns);
    if (markUnknown) {
      await markUnknown(currentStep.fieldId);
    }
    if (touchField) {
      touchField(currentStep.fieldId);
    }
    analyticsTrack("survey_field_unknown", {
      companyId: vendorId,
      surveyYear: selectedYear,
      fieldId: currentStep.fieldId
    });
    if (onNextStep) {
      onNextStep();
    }
  };

  const scheduleAutoAdvanceIfApplicable = ({
    updates,
    advanceStep,
    fieldId,
    response
  }) => {
    if (advanceStep) {
      return;
    }
    if (!updates.length) {
      return;
    }
    if (!fieldId) {
      return;
    }
    if (!advanceCurrentStep) {
      return;
    }
    if (!isAckOnlyResponse(response)) {
      return;
    }
    pendingAutoAdvanceRef.current = { fieldId };
  };

  const dispatchScoutResponse = async ({
    fieldUpdates,
    advanceStep,
    markUnknown: shouldMarkUnknown,
    skip: shouldSkip,
    response
  }) => {
    const fieldId = currentStep?.fieldId;

    if (shouldMarkUnknown && fieldId && markUnknown) {
      await markUnknown(fieldId);
      if (touchField) {
        touchField(fieldId);
      }
      analyticsTrack("survey_field_unknown", {
        companyId: vendorId,
        surveyYear: selectedYear,
        fieldId
      });
      if (onNextStep) {
        onNextStep();
      }
      return;
    }

    if (shouldSkip && fieldId && skipQuestion) {
      await skipQuestion(fieldId);
      if (touchField) {
        touchField(fieldId);
      }
      analyticsTrack("survey_field_skipped", {
        companyId: vendorId,
        surveyYear: selectedYear,
        fieldId
      });
      if (onNextStep) {
        onNextStep();
      }
      return;
    }

    const updates = fieldUpdates || [];
    updates.map(handleFieldUpdate);
    if (!advanceStep || !advanceCurrentStep) {
      scheduleAutoAdvanceIfApplicable({
        updates,
        advanceStep,
        fieldId,
        response
      });
      return;
    }
    if (fieldId && touchField) {
      touchField(fieldId);
    }
    if (fieldId && clearSkipUnknownMarkers) {
      clearSkipUnknownMarkers(fieldId);
    }
    const deferAdvance = updates.length > 0;
    const fireAdvance = deferAdvance
      ? () => setTimeout(advanceCurrentStep, 0)
      : advanceCurrentStep;
    fireAdvance();
  };

  const sendMessage = async (messageOverride) => {
    const trimmed = (messageOverride || input).trim();
    if (!trimmed || isLoading || !canEdit) {
      return;
    }

    const conversationId = await ensureConversationId();
    const currentValues = buildCurrentValuesSnapshot(context, currentStep);

    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setInput("");
    setIsLoading(true);

    analyticsTrack("survey_scout_message_sent", {
      companyId: vendorId,
      surveyYear: selectedYear,
      fieldId: currentStep?.fieldId
    });

    try {
      await fetchOurApi({
        path: "/agents/chat-survey",
        method: "POST",
        data: {
          companyId: vendorId,
          conversationId,
          message: trimmed,
          currentStep: currentStep
            ? { ...currentStep, surveyYear: selectedYear }
            : null,
          currentValues
        },
        callback: async (result) => {
          const safeResult = result || {};
          const hasAction =
            safeResult.markUnknown ||
            safeResult.skip ||
            (safeResult.fieldUpdates || []).length > 0 ||
            safeResult.advanceStep;
          const restatedQuestion = buildStepQuestion(currentStep, { context });
          const noActionFallback = restatedQuestion
            ? `Sorry, I didn't quite catch that. Let me ask again — ${restatedQuestion}`
            : "Sorry, I didn't quite catch that. Could you rephrase?";
          const content =
            safeResult.response || (hasAction ? "Got it." : noActionFallback);
          setMessages((prev) => [...prev, { role: "assistant", content }]);
          await dispatchScoutResponse(safeResult);
        }
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { feature: "scout-chat-survey" },
        extra: { surveyYear: selectedYear, fieldId: currentStep?.fieldId }
      });
      const restatedQuestion = buildStepQuestion(currentStep, { context });
      const errorFallback = restatedQuestion
        ? `Sorry, I hit a snag connecting. Let me try the question again — ${restatedQuestion}`
        : "Sorry, I hit a snag connecting. Could you try again?";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: errorFallback }
      ]);
    }

    setIsLoading(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleToggleCollapse = () => {
    const next = !isScoutCollapsed;
    if (setIsScoutCollapsed) {
      setIsScoutCollapsed(next);
    }
    analyticsTrack(next ? "survey_scout_collapsed" : "survey_scout_expanded", {
      companyId: vendorId,
      surveyYear: selectedYear
    });
  };

  const transcriptMessages = messages;

  const handleContinue = async () => {
    if (!canEdit) {
      return;
    }
    const echoLabel = continueLabel || "Continue";
    const continueTurns = [{ role: "user", content: echoLabel }];
    setMessages((prev) => [...prev, ...continueTurns]);
    await persistTurnsToConversation(continueTurns);
    if (currentStep?.fieldId) {
      if (touchField) {
        touchField(currentStep.fieldId);
      }
      if (clearSkipUnknownMarkers) {
        clearSkipUnknownMarkers(currentStep.fieldId);
      }
    }
    if (advanceCurrentStep) {
      advanceCurrentStep();
    }
  };

  const showSkipControls = !!currentStep?.fieldId && !!skipQuestion;
  const showContinue = !!pendingAdvanceDisplayValue && !!advanceCurrentStep;
  const continueLabel = currentStepCarriedOver
    ? "Yes, still correct"
    : "Continue";

  const sharedBodyProps = {
    messages: transcriptMessages,
    isLoading,
    input,
    setInput,
    onKeyDown,
    canEdit,
    showSkipControls,
    showContinue,
    continueLabel,
    onSkip: handleSkip,
    onUnknown: handleUnknown,
    onContinue: handleContinue
  };

  return (
    <>
      {isScoutCollapsed ? (
        <Box
          sx={{ display: { xs: "none", md: "flex" } }}
          flexDirection="column"
          style={{
            width: RAIL_WIDTH,
            flexShrink: 0,
            background: palette.common.white,
            borderRight: `1px solid ${palette.backgroundGray.dark}`,
            transition: "width 200ms ease",
            height: "100%"
          }}
        >
          <PanelHeader
            isOpen={false}
            onToggle={handleToggleCollapse}
            onClose={handleToggleCollapse}
            showCloseIcon={false}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flex: "1 1 60%",
            maxWidth: "60%",
            minWidth: 360
          }}
          flexDirection="column"
          style={{
            background: palette.common.white,
            borderRight: `1px solid ${palette.backgroundGray.dark}`,
            transition: "width 200ms ease",
            height: "100%"
          }}
        >
          <PanelHeader
            isOpen
            onToggle={handleToggleCollapse}
            onClose={handleToggleCollapse}
            showCloseIcon={false}
          />
          <PanelBody
            {...sharedBodyProps}
            transcriptRef={desktopTranscriptRef}
            inputRef={desktopReplyInputRef}
          />
        </Box>
      )}

      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          width: "100%",
          flexGrow: 1
        }}
        flexDirection="column"
        style={{
          background: palette.common.white,
          height: "100%",
          minHeight: 0
        }}
      >
        <PanelHeader
          isOpen
          onToggle={handleToggleCollapse}
          onClose={handleToggleCollapse}
          showCloseIcon={false}
        />
        <PanelBody
          {...sharedBodyProps}
          transcriptRef={mobileTranscriptRef}
          inputRef={mobileReplyInputRef}
        />
      </Box>
    </>
  );
};
export default SurveyScoutPanel;
